import {
    Box,
    Chip,
    Pagination,
    Paper,
    Skeleton,
    Typography,
    useTheme,
} from "@mui/material";
import { observer } from "mobx-react";
import React from "react";
import type ResearchFindingsStore from "stores/ResearchFindingsStore";
import {
    formatRunDate,
    formatRunTime,
    runStatusColor,
} from "./researchFindingsUtils";

type Props = {
    store: ResearchFindingsStore;
    onPageChange: (page: number) => void;
    onSelectRun: (runId: string) => void;
};

/** Matches ResearchFindingsStore.RUNS_PAGE_SIZE — number of skeleton cards to show while loading. */
const SKELETON_RUN_COUNT = 10;

/** Left panel: the list of research runs. Clicking a run lazily loads its detail. */
const RunListPanel: React.FC<Props> = observer(
    ({ store, onPageChange, onSelectRun }) => {
        const theme = useTheme();
        const skeletonBg = { backgroundColor: theme.palette.gray[300] };

        return (
            <Box
                sx={{
                    width: "280px",
                    flexShrink: 0,
                    position: "sticky",
                    top: theme.spacing(2),
                    alignSelf: "flex-start",
                    maxHeight: `calc(100vh - ${theme.spacing(4)})`,
                    overflowY: "auto",
                }}
            >
                <Typography
                    sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: theme.palette.gray[600],
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        mb: 1,
                    }}
                >
                    Research runs
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {store.isLoading
                        ? Array.from({ length: SKELETON_RUN_COUNT }, (_, i) => (
                              <Paper
                                  key={`run-skeleton-${i}`}
                                  elevation={0}
                                  sx={{
                                      padding: theme.spacing(1.5),
                                      borderRadius: "8px",
                                      border: `1px solid ${theme.palette.gray[200]}`,
                                  }}
                              >
                                  <Box
                                      sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          gap: 1,
                                      }}
                                  >
                                      <Skeleton
                                          variant="text"
                                          width="45%"
                                          height={20}
                                          sx={skeletonBg}
                                      />
                                      <Skeleton
                                          variant="rounded"
                                          width={64}
                                          height={20}
                                          sx={skeletonBg}
                                      />
                                  </Box>
                                  <Skeleton
                                      variant="text"
                                      width="60%"
                                      height={16}
                                      sx={{ ...skeletonBg, mt: 0.5 }}
                                  />
                              </Paper>
                          ))
                        : store.runs.map((run) => {
                              const selected = run.id === store.selectedRunId;
                              return (
                                  <Paper
                                      key={run.id}
                                      elevation={0}
                                      onClick={() => onSelectRun(run.id)}
                                      sx={{
                                          cursor: "pointer",
                                          padding: theme.spacing(1.5),
                                          borderRadius: "8px",
                                          border: `1px solid ${
                                              selected
                                                  ? theme.palette.primary.main
                                                  : theme.palette.gray[200]
                                          }`,
                                          backgroundColor: selected
                                              ? theme.palette.gray[100]
                                              : theme.palette.white.main,
                                          "&:hover": {
                                              borderColor:
                                                  theme.palette.primary.main,
                                          },
                                      }}
                                  >
                                      <Box
                                          sx={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                              gap: 1,
                                          }}
                                      >
                                          <Typography
                                              sx={{
                                                  fontSize: "14px",
                                                  fontWeight: 600,
                                                  color: theme.palette
                                                      .gray[700],
                                              }}
                                          >
                                              {formatRunDate(run.startedAtUtc)}
                                          </Typography>
                                          {run.status && (
                                              <Chip
                                                  size="small"
                                                  label={run.status}
                                                  color={runStatusColor(
                                                      run.status,
                                                  )}
                                                  variant="outlined"
                                              />
                                          )}
                                      </Box>
                                      <Typography
                                          sx={{
                                              fontSize: "12px",
                                              color: theme.palette.gray[500],
                                              mt: 0.25,
                                          }}
                                      >
                                          {formatRunTime(run.startedAtUtc)}
                                      </Typography>
                                      <Typography
                                          sx={{
                                              fontSize: "12px",
                                              color: theme.palette.gray[500],
                                              mt: 0.5,
                                          }}
                                      >
                                          {run.questionCount} question
                                          {run.questionCount === 1 ? "" : "s"}
                                          {run.answerCount > 0 &&
                                              ` · ${run.answerCount} answer${
                                                  run.answerCount === 1
                                                      ? ""
                                                      : "s"
                                              }`}
                                      </Typography>
                                  </Paper>
                              );
                          })}
                </Box>
                {store.runsPageCount > 1 && (
                    <Pagination
                        size="small"
                        count={store.runsPageCount}
                        page={store.runsPage}
                        disabled={store.isLoading}
                        onChange={(_, page) => {
                            onPageChange(page);
                        }}
                        sx={{
                            mt: 1.5,
                            display: "flex",
                            justifyContent: "center",
                        }}
                    />
                )}
            </Box>
        );
    },
);

export default RunListPanel;
